import React from 'react';
import {
  Component,
  Dispatcher
} from "puzzle";
import {
  Notification,
  NotificationGroup
} from '@progress/kendo-react-notification';
import uuid from 'uuid-random';
import { Fade } from "@progress/kendo-react-animation";
import {
  capitalize,
  get,
  has,
  includes,
  isArray
} from "lodash";
import { COMPLETE, RETURN, MAS_CLAIM_FAILED_TASK, CLAIM_NOTIFICATION_DATA, MASS_START_RESPONSE_DATA } from "./Types";

export type TOASTER_TYPE = 'none' | 'success' | 'error' | 'warning' | 'info'

export type NOTIFICATION = {
  className?: string
  closable?: boolean
  customConfig?: any
  id?: string
  icon?: boolean
  isDownloadNotification?: boolean
  text?: string
  type?: TOASTER_TYPE
};

export type TOAST_STATE = {
  notifications: NOTIFICATION[]
}

export default class ToastNotifications extends Component<{}, TOAST_STATE> {
  private notificationStack: NOTIFICATION[] = []; //Added to support PDII-34945
  state: TOAST_STATE = {
    notifications: []
  };

  /**
   * Removes files processing notification, and adds notifications for completed/failed targets
   * @param notification
   * @param response
   */
  addReopenFinishedNotifications = (notification: any, response: any) => {
    const {
      reopenTargets = [],
      failedTargets = [],
      deliveredViaWebServices = [],
    } = response;
    let newList = this.state.notifications.filter((item: NOTIFICATION) => item.id !== notification.id);

    let idsForRemoval: { id: string }[] = [];
    if (reopenTargets.length || deliveredViaWebServices.length) {
      const id = `${notification.id}_success`;
      idsForRemoval = idsForRemoval.concat({ id });
      newList = newList.concat({
        id: id,
        text: this.translate('reopen.complete', { files: reopenTargets.length || deliveredViaWebServices.length }),
        type: 'success',
        icon: true,
        closable: true
      });
    }

    if (failedTargets.length) {
      const id = `${notification.id}_error`;
      idsForRemoval = idsForRemoval.concat({ id });
      newList = newList.concat({
        id,
        text: this.translate('reopen.error', { files: failedTargets.length }),
        type: 'error',
        icon: true,
        closable: true
      });
    }

    this.setState({
      notifications: newList
    }, () => {
      setTimeout(this.removeNotificationFromList.bind(this, idsForRemoval), 6000);
    });
  }

  /**
   * Add new notification to notificationStack
   * NOTE: Need to call runStackedNotifications method to show all notifications from stack
   * @param notificationData
   */
  addToNotificationStack = (notificationData: NOTIFICATION) => {
    const notification: NOTIFICATION = {
      ...notificationData,
      id: uuid()
    };

    this.notificationStack.push(notification);
  }

  /**
   * Creates an object for notification set - Complete action / Assign action
   * @param responseData
   * @param flow
   */
  createCompleteNotifications = (responseData: COMPLETE | RETURN, flow = ''): void => {
    let fileNumbers;
    let key;

    if (has(responseData, 'numberOfSuccessfulTargets')) {
      fileNumbers = (responseData as COMPLETE).numberOfSuccessfulTargets;
      key = 'promote';
    } else {
      fileNumbers = (responseData as RETURN).numberOfReturnedTargets;
      key = 'demote';
    }

    let text = this.translate(`${key}Success`, { file: fileNumbers });
    if (flow === 'assign') {
      text = this.translate(`fileAssign`, { file: fileNumbers });
    }

    let promoteNotification: NOTIFICATION = {
      text,
      type: 'success',
      icon: true,
      closable: true
    };
    this.notificationStack.push({
      ...promoteNotification,
      id: uuid()
    })
  };

  /**
   * Create download notification
   */
  createDownloadNotification = () => {
    let notification: NOTIFICATION = {
      className: 'tp-notifications-download',
      closable: true,
      icon: true,
      isDownloadNotification: true,
      text: this.translate('downloadNotification'),
      type: 'none'
    };

    this.setNotification(notification);
  }

  /**
   * Creates toaster for failed action
   * @param text
   * @param type
   */
  createToaster = (text: string, type: TOASTER_TYPE) => {
    let deliverablesDownloadFailedNotification: NOTIFICATION = {
      closable: true,
      icon: true,
      text,
      type
    };

    this.setNotification(deliverablesDownloadFailedNotification);
  }

  /**
   * Mass claim failed notification
   *
   * @param notificationCase
   * @param failedTasks
   * @param id
   */
  createMassClaimFailedNotifications = (notificationCase: string, failedTasks: MAS_CLAIM_FAILED_TASK[], id: string) => {
    let text = '';
    switch (notificationCase) {
      case "claim":
        text = this.translate('claimFailedNotificationText', {
          tasks: failedTasks.length
        })
        break;
      case "unclaim":
        text = this.translate('unclaimFailedNotificationText', {
          tasks: failedTasks.length
        })
        break;
      default:
        break;
    }
    let claimNotification: NOTIFICATION = {
      id,
      closable: true,
      customConfig: {
        notificationCase,
        notificationData: failedTasks,
        type: 'link'
      },
      icon: true,
      text,
      type: 'warning'
    };

    return claimNotification;
  };

  /**
   * Create mass Claim Toast notification on Supplier view
   */
  createMassSupplierClaimNotifications = ({ notificationCase, taskPhaseLength, id }: any) => {
    let text = '';
    switch (notificationCase) {
      case "claim":
        text = this.translate('claimNotificationText', { tasks: taskPhaseLength })
        break;
      case "unclaim":
        text = this.translate('unclaimNotificationText', { tasks: taskPhaseLength })
        break;
      default:
        break;
    }
    let claimNotification: NOTIFICATION = {
      closable: true,
      id,
      icon: true,
      text,
      type: 'success'
    };

    return claimNotification;
  };

  /**
   * Promote/demote Toast notification
   *
   * @param actionName
   * @param response
   */
  createPromoteNotification = (response: any, actionName: string) => {
    const numberOfFiles = response.promotedTargetIds?.length || response.demotedTargetIds?.length || 0;
    if (numberOfFiles) {
      let localeKey = 'promoteSuccess';
      if (actionName === 'demote') {
        localeKey = 'demoteSuccess';
      }

      const promoteNotification: NOTIFICATION = {
        text: this.translate(localeKey, { file: numberOfFiles }),
        type: 'success',
        icon: true,
        closable: true
      };

      this.notificationStack.push({
        ...promoteNotification,
        id: uuid()
      })
    }
  };

  /**
   * Creates an object for notification set
   */
  createPreferenceNotifications = () => {
    let promoteNotification: NOTIFICATION = {
      text: this.translate('notificationText'),
      type: 'success',
      icon: true,
      closable: true
    };

    this.setNotification(promoteNotification);
  };

  /**
   * Creates Claim/Unclaim Toast notification
   * @param notificationCase
   * @param taskPhaseLength
   */
  createClaimNotifications = ({ notificationCase, taskPhaseLength }: CLAIM_NOTIFICATION_DATA): void => {
    let text = '';
    switch (notificationCase) {
      case "claim":
        text = this.translate('claimNotificationText', { tasks: taskPhaseLength });
        break;
      case "unclaim":
        text = this.translate('unclaimNotificationText', { tasks: taskPhaseLength });
        break;
      default:
        break;
    }
    let claimNotification: NOTIFICATION = {
      text: text,
      type: 'success',
      icon: true,
      closable: true
    };

    this.setNotification(claimNotification);
  };

  /**
   * Quick claim notification
   *
   * @param numberOfSuccessfulTargets
   */
  createQuickClaimNotifications = ({ numberOfSuccessfulTargets }: { numberOfSuccessfulTargets: number }) => {
    let claimNotification: NOTIFICATION = {
      text: this.translate('quickClaimNotificationText', { files: numberOfSuccessfulTargets }),
      type: 'success',
      icon: true,
      closable: true
    };

    this.notificationStack.push({
      ...claimNotification,
      id: uuid()
    })
  };

  /**
   * Create claim and reserve toast notification on Supplier view
   */
  createSupplierClaimAndReserveNotifications = ({ reservePhaseLength }: any) => {
    let claimNotification: NOTIFICATION = {
      text: this.translate('claimAndReserve', { tasks: reservePhaseLength }),
      type: 'success',
      icon: true,
      closable: true
    };

    this.setNotification(claimNotification);
  };

  /**
   * Returns TSC upload success notification
   * @param {uploadFileCount}
   */
  createTscNotification = ({ uploadFileCount }: { uploadFileCount: number }): void => {
    if (uploadFileCount) {
      let tscUpload: NOTIFICATION = {
        text: this.translate('uploadTSC', { file: uploadFileCount }),
        type: 'success',
        icon: true,
        closable: true
      };

      this.setNotification(tscUpload);
    }
  };

  /**
   * Returns TSC upload success notification
   * @param gradedFileCount
   */
  createGradeAndScoreNotification = (gradedFileCount: number): void => {
    if (gradedFileCount) {
      let gradeAndScore: NOTIFICATION = {
        text: this.translate('gradingAndScoring', { file: gradedFileCount }),
        type: 'success',
        icon: true,
        closable: true
      };

      this.notificationStack.push({
        ...gradeAndScore,
        id: uuid()
      })
    }
  };

  /**
   * Handle mass start notifications
   * @param responseData
   * @param processingNotificationId
   */
  handleMassStartNotificationsOnFinish = (responseData: MASS_START_RESPONSE_DATA, processingNotificationId: string) => {
    const newList = this.state.notifications.filter((item: NOTIFICATION) => item.id !== processingNotificationId);
    const newListIds: NOTIFICATION[] = [];

    if (!!responseData.numberOfStarted) {
      const successId = `${processingNotificationId}_success`;
      const successNotification: NOTIFICATION = {
        closable: true,
        id: successId,
        icon: true,
        text: this.translate('massStart.success', { count: responseData.numberOfStarted }),
        type: 'success'
      };
      newList.push(successNotification);
      newListIds.push({ id: successId });
    }

    if (!!responseData.numberOfFailed) {
      const failedId = `${processingNotificationId}_failed`;
      const failedNotification: NOTIFICATION = {
        id: failedId,
        closable: true,
        customConfig: {
          notificationCase: 'massStart',
          notificationData: responseData.startSubmissionErrors,
          type: 'link'
        },
        icon: true,
        text: this.translate('massStart.failed', { count: responseData.numberOfFailed }),
        type: 'warning'
      };

      newList.push(failedNotification);
      newListIds.push({ id: failedId });
    }

    this.setState({
      notifications: newList
    }, () => {
      setTimeout(this.removeNotificationFromList.bind(this, newListIds), 6000);
    });
  }

  /**
   * Removes progress notification and displays successful/failed ones
   *
   * @param notification
   * @param ignoredTasksForMassAction
   * @param taskIds
   * @param notificationName
   * @param linkedTasksNumber number of linked tasks that should be added to total checked list
   */
  handleMultiClaimNotificationsOnFinish = (notification: {
    id: string,
    notificationName: string
  }, { ignoredTasksForMassAction = [] }: {
    ignoredTasksForMassAction: MAS_CLAIM_FAILED_TASK[]
  }, { taskIds }: {
    taskIds: string[]
  }, linkedTasksNumber: number = 0) => {
    let newList = this.state.notifications.filter((item: NOTIFICATION) => item.id !== notification.id);
    let idsForRemoval: { id: string }[] = [];
    if (ignoredTasksForMassAction?.length) {
      const id = notification.id + 'failed';
      idsForRemoval = idsForRemoval.concat({ id });
      newList = newList.concat(this.createMassClaimFailedNotifications(notification.notificationName, ignoredTasksForMassAction, id));
    }
    linkedTasksNumber = notification.notificationName === 'claim' ? linkedTasksNumber : 0; // for unclaim case linked tasks are already included in taskIds array (request from SS)
    const successfullyClaimed = (taskIds.length + linkedTasksNumber) - ignoredTasksForMassAction.length;
    if (successfullyClaimed > 0) {
      idsForRemoval = idsForRemoval.concat({ id: notification.id });
      newList = newList.concat(this.createMassSupplierClaimNotifications({
        notificationCase: notification.notificationName,
        taskPhaseLength: successfullyClaimed,
        id: notification.id
      }));
    }
    this.setState({
      notifications: newList
    }, () => {
      setTimeout(this.removeNotificationFromList.bind(this, idsForRemoval), 6000);
    });
  };

  /**
   * Refreshes main grid after successfully ping on promote/demote to update submission status
   * Listener for post message is located on dispatcher PopupWindow
   */
  refreshMainGridByPushMessage = () => {
    window.opener?.postMessage({
      actionName: 'refreshMainGridOnCurrentPosition'
    }, window.location.href);
  };

  /**
   * Used for refreshing detached drawer data.
   * Listener for post message is located on dispatcher PopupWindow
   */
  refreshChildPopupByPushMessage = () => {
    let popUpWindow = get(Dispatcher, 'appModules.PopupWindow[0].popupWindow');
    popUpWindow?.postMessage({
      actionName: 'refreshDrawerTab'
    }, window.location.href);
  };

  /**
   * Remove download notification on request fail
   */
  removeDownloadNotification = () => {
    const newList = this.state.notifications.filter((item: NOTIFICATION) => !item.isDownloadNotification);
    this.setState({
      notifications: newList
    });
  }

  /**
   * Removes notification form list
   * @param notification
   */
  removeNotificationFromList = (notification: NOTIFICATION | NOTIFICATION[]): void => {
    let idList: (string | undefined) [] = [];
    if (isArray(notification)) {
      idList = notification.map(item => item.id);
    }
    const newList = this.state.notifications.filter((item: NOTIFICATION) => idList.length ? !includes(idList, item.id) : item.id !== (notification as NOTIFICATION).id);
    this.setState({
      notifications: newList
    })
  };

  /**
   * Returns notification list
   */
  renderNotifications = (): JSX.Element[] => {
    const { notifications } = this.state;

    let notificationList = notifications.map(((notification: NOTIFICATION) => {
      const {
        className = '',
        closable,
        icon,
        id,
        text = '',
        type,
        customConfig = undefined
      } = notification;
      let notificationRender = (<></>);
      if (!customConfig) {
        notificationRender = (
          <Notification
            className={`tp-notification-${type} ${className}`}
            style={{
              pointerEvents: 'all'
            }}
            key={id}
            type={{
              style: type,
              icon: icon
            }}
            {...(closable && {
              closable,
              onClose: this.removeNotificationFromList.bind(this, notification)
            })}
          >
            <span
              className={'tp-text-medium'}
              role={'alert'}
              dangerouslySetInnerHTML={{ __html: text }}
            />
          </Notification>
        );
      } else {
        switch (customConfig.type) {
          case 'processing':
            notificationRender = (
              <Notification
                key={id}
                className={`tp-notification-${customConfig.type}`}
                closable={true}
                type={{
                  style: "none",
                  icon: true
                }}
              >
                <span className={'tp-text-medium'}
                      dangerouslySetInnerHTML={{ __html: text }}
                />
              </Notification>
            );
            break;
          case 'link':
            notificationRender = (
              <Notification
                className={`tp-notification-${type}`}
                style={{
                  pointerEvents: 'all'
                }}
                key={id}
                type={{
                  style: type,
                  icon: icon
                }}
                {...(closable && {
                  closable,
                  onClose: this.removeNotificationFromList.bind(this, notification)
                })}
              >
                <span
                  className={'tp-text-medium'}
                  role={'alert'}
                >
                  <span className={'k-pr-1'} dangerouslySetInnerHTML={{ __html: text }} />
                  <span className={'tp-text-underline tp-pointable tp-text-semibold'}
                        onClick={this.showFailedNotificationDetails.bind(this, notification)}>{this.translate('showDetails')}</span>
                </span>
              </Notification>
            );
            break;
          default:
            break
        }
      }

      return notificationRender;
    }));

    return notificationList;
  };

  /**
   * Fires all notifications stacked/saved in previous iterations
   */
  runStackedNotifications = () => {
    const notifications = this.state.notifications.concat(this.notificationStack);
    const notificationToRemove = [...this.notificationStack];
    this.notificationStack = [];
    this.setState({
      notifications
    }, () => {
      setTimeout(this.removeNotificationFromList.bind(this, notificationToRemove), 6000);
    })
  };

  /**
   * Add's new notification to notification list
   * @param notificationData
   */
  setNotification = (notificationData: NOTIFICATION): void => {
    const notification: NOTIFICATION = {
      ...notificationData,
      id: uuid()
    };

    this.setState({
      notifications: this.state.notifications.concat(notification)
    }, () => {
      setTimeout(this.removeNotificationFromList.bind(this, notification), 6000);
    })
  };

  /**
   * Add's new notification for processing request
   * @param notificationId
   */
  setProcessingNotification = (notificationId: string): void => {
    const notification: NOTIFICATION = {
      text: this.translate('processingRequest'),
      id: notificationId,
      customConfig: {
        type: 'processing'
      }
    };

    this.setState({
      notifications: this.state.notifications.concat(notification)
    });
  };

  /**
   * Add's new notification to notification list
   * @param notificationId
   */
  setReopenProcessingNotification = (notificationId: string): void => {
    const notification: NOTIFICATION = {
      text: this.translate('filesAreProcessing'),
      id: notificationId,
      customConfig: {
        type: 'processing'
      }
    };

    this.setState({
      notifications: this.state.notifications.concat(notification)
    });
  };

  /**
   * Displays a dialog with information of the failed tasks
   *
   * @param notification
   */
  showFailedNotificationDetails = (notification: NOTIFICATION) => {
    this.removeNotificationFromList(notification);
    const notificationCase = notification.customConfig?.notificationCase;
    switch (notificationCase) {
      case 'claim':
      case 'unclaim':
        const claimSwitch = notification.customConfig?.notificationCase || 'claim';
        this.dispatch('showMultiClaimFailedTasks', '', {
          data: notification.customConfig.notificationData,
          claimSwitch: capitalize(claimSwitch)
        });
        break;
      case 'massStart':
        this.dispatch('showMassStartFailedSubmissions', '', {
          data: notification.customConfig.notificationData
        })
        break;
      default:
        break;
    }

  };

  render() {
    return (
      <NotificationGroup
        style={{
          zIndex: 14000,
          bottom: '2.5%',
          right: '1%',
          alignItems: 'flex-end',
        }}
      >
        <Fade enter={true} exit={true}>
          {this.renderNotifications()}
        </Fade>
      </NotificationGroup>
    );
  }
}

